import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  Euro, 
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Target,
  BarChart3,
  Calendar,
  ArrowUpRight,
  Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@plvplus/core';
import { User } from '../../hooks/useUserRoles';
import { PriPlusOverview } from './widgets/PriPlusOverview';

interface ManagerDashboardProps {
  user: User;
}

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ user }) => {
  // Mock manager data
  const teamStats = [
    {
      name: 'Équipe',
      value: '8',
      change: '+1',
      changeType: 'positive' as const,
      icon: Users,
      description: 'Membres actifs'
    },
    {
      name: 'CA ce mois',
      value: '€145K',
      change: '+18%',
      changeType: 'positive' as const,
      icon: Euro,
      description: 'Objectif: €160K'
    },
    {
      name: 'Devis en cours',
      value: '23',
      change: '+5',
      changeType: 'positive' as const,
      icon: FileText,
      description: 'À traiter'
    },
    {
      name: 'Taux conversion',
      value: '72%',
      change: '+8%',
      changeType: 'positive' as const,
      icon: TrendingUp,
      description: 'Objectif: 75%'
    }
  ];

  const pendingApprovals = [
    {
      id: '1',
      type: 'quote',
      title: 'Devis PLV Pharmacie Central',
      amount: '€2,450',
      requestedBy: 'Marie Dubois',
      time: '2h',
      priority: 'high'
    },
    {
      id: '2',
      type: 'expense',
      title: 'Matériaux présentoir Carrefour',
      amount: '€850',
      requestedBy: 'Pierre Martin',
      time: '4h',
      priority: 'medium'
    },
    {
      id: '3',
      type: 'discount',
      title: 'Remise client fidèle - Bio&Co',
      amount: '€320',
      requestedBy: 'Sophie Laurent',
      time: '1j',
      priority: 'low'
    }
  ];

  const teamPerformance = [
    {
      name: 'Marie Dubois',
      role: 'Commercial Senior',
      thisMonth: '€28K',
      target: '€25K',
      completion: 112,
      status: 'excellent'
    },
    {
      name: 'Pierre Martin',
      role: 'Technico-commercial',
      thisMonth: '€22K',
      target: '€20K',
      completion: 110,
      status: 'excellent'
    },
    {
      name: 'Sophie Laurent',
      role: 'Commercial Junior',
      thisMonth: '€15K',
      target: '€18K',
      completion: 83,
      status: 'attention'
    }
  ];

  const getApprovalIcon = (type: string) => {
    switch (type) {
      case 'quote': return FileText;
      case 'expense': return Euro;
      case 'discount': return Target;
      default: return AlertCircle;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-red-500 bg-red-50 dark:bg-red-900/10';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10';
      default: return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10';
    }
  };

  const getPerformanceColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-green-600 dark:text-green-400';
      case 'good': return 'text-blue-600 dark:text-blue-400';
      case 'attention': return 'text-yellow-600 dark:text-yellow-400';
      default: return 'text-red-600 dark:text-red-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Manager Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Tableau de bord Manager 📊
            </h1>
            <p className="text-blue-100">
              Bienvenue {user.name.split(' ')[0]}, voici un aperçu de votre équipe et des performances.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <p className="text-sm text-blue-200">Niveau d'accès</p>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                Manager
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {teamStats.map((stat) => (
          <Card key={stat.name} variant="elevated">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.name}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {stat.description}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                  <stat.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
                  vs mois dernier
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* PRI+ Overview for Managers */}
      <div className="mb-6">
        <PriPlusOverview compact={true} showQuickActions={false} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Approvals */}
        <Card variant="elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Approbations en attente</span>
              </CardTitle>
              <Badge variant="destructive">{pendingApprovals.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingApprovals.map((approval) => {
                const Icon = getApprovalIcon(approval.type);
                return (
                  <div
                    key={approval.id}
                    className={`border-l-4 rounded-r-lg p-4 transition-all hover:shadow-sm ${getPriorityColor(approval.priority)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            {approval.title}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Par {approval.requestedBy} • Il y a {approval.time}
                          </p>
                          <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 mt-1">
                            {approval.amount}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline">
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <AlertCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Team Performance */}
        <Card variant="elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Performance équipe</span>
              </CardTitle>
              <Button variant="outline" size="sm">
                Rapport complet
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {teamPerformance.map((member) => (
                <div key={member.name} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {member.name}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {member.role}
                      </p>
                    </div>
                    <div className={`text-sm font-semibold ${getPerformanceColor(member.status)}`}>
                      {member.completion}%
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>Réalisé: <strong>{member.thisMonth}</strong></span>
                    <span>Objectif: {member.target}</span>
                  </div>
                  <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        member.completion >= 100 
                          ? 'bg-green-500' 
                          : member.completion >= 80 
                          ? 'bg-blue-500' 
                          : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(member.completion, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manager Quick Actions */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Actions de gestion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    Rapports
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Analytics équipe
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
              </div>
            </div>

            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    Objectifs
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Définir cibles
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
              </div>
            </div>

            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    Planning
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Organiser équipe
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
              </div>
            </div>

            <Link
              to="/pri-plus"
              className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-blue-300 dark:hover:border-blue-600"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    Validation PRI+
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Approuver calculs
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
